// utils/comparisonEngine.js - Smart submission comparison analysis engine
import { isEqual } from 'lodash';

// Comparison result types
export const COMPARISON_TYPES = {
  PERFECT_MATCH: 'PERFECT_MATCH',
  SCORE_MATCH_STATS_DIFFER: 'SCORE_MATCH_STATS_DIFFER',
  LINEUP_MISMATCH: 'LINEUP_MISMATCH', 
  STATS_TOTAL_MISMATCH: 'STATS_TOTAL_MISMATCH',
  MVP_CONFLICT: 'MVP_CONFLICT',
  EVIDENCE_MISMATCH: 'EVIDENCE_MISMATCH',
  COMPLETELY_DIFFERENT: 'COMPLETELY_DIFFERENT',
  PARTIAL_SUBMISSION: 'PARTIAL_SUBMISSION'
};

// Conflict severity levels
export const CONFLICT_SEVERITY = {
  NONE: 'none',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Field comparison types
export const FIELD_COMPARISON = {
  MATCH: 'match',
  DIFFER: 'differ',
  MISSING: 'missing',
  INVALID: 'invalid'
};

/**
 * Core comparison engine for tournament match submissions
 * Analyzes two team submissions and provides detailed comparison results
 */
export class ComparisonEngine {
  constructor(team1Submission, team2Submission, teamRosters = {}) {
    this.team1Data = team1Submission?.match_data || team1Submission;
    this.team2Data = team2Submission?.match_data || team2Submission;
    this.team1Roster = teamRosters.team1 || [];
    this.team2Roster = teamRosters.team2 || [];
    
    // Cache for expensive operations
    this.analysisCache = new Map();
  }

  /**
   * Performs comprehensive comparison analysis
   * @returns {Object} Complete comparison analysis
   */
  analyzeSubmissions() {
    const cacheKey = 'full_analysis';
    if (this.analysisCache.has(cacheKey)) {
      return this.analysisCache.get(cacheKey);
    }


    // Handle partial submissions
    if (!this.team1Data || !this.team2Data) {
      const result = this.handlePartialSubmission();
      this.analysisCache.set(cacheKey, result);
      return result;
    }

    // Perform detailed comparison
    const scoreComparison = this.compareScores();
    const playerStatsComparison = this.comparePlayerStats();
    const lineupComparison = this.compareLineups();
    const mvpComparison = this.compareMVPs();
    const evidenceComparison = this.compareEvidence();
    const statisticalValidation = this.validateStatistics();

    // Determine overall comparison type and severity
    const overallType = this.determineComparisonType({
      scoreComparison,
      playerStatsComparison,
      lineupComparison,
      mvpComparison,
      evidenceComparison,
      statisticalValidation
    });

    const severity = this.calculateSeverity({
      scoreComparison,
      playerStatsComparison,
      lineupComparison,
      mvpComparison,
      evidenceComparison,
      statisticalValidation
    });

    const result = {
      comparisonType: overallType,
      severity,
      autoApprovable: overallType === COMPARISON_TYPES.PERFECT_MATCH,
      requiresManualReview: severity !== CONFLICT_SEVERITY.NONE,
      
      // Detailed comparisons
      scores: scoreComparison,
      playerStats: playerStatsComparison,
      lineups: lineupComparison,
      mvp: mvpComparison,
      evidence: evidenceComparison,
      statistical: statisticalValidation,
      
      // Smart recommendations
      recommendations: this.generateRecommendations(overallType, severity),
      conflictSummary: this.generateConflictSummary({
        scoreComparison,
        playerStatsComparison,
        lineupComparison,
        mvpComparison,
        evidenceComparison
      })
    };

    this.analysisCache.set(cacheKey, result);
    return result;
  }

  /**
   * Handles partial submission scenarios
   */
  handlePartialSubmission() {
    const availableData = this.team1Data || this.team2Data;
    const missingTeam = !this.team1Data ? 'team1' : 'team2';
    
    // Analyze available data for display purposes
    const partialPlayerStats = this.analyzePartialPlayerStats(availableData, missingTeam);
    const partialLineups = this.analyzePartialLineups(availableData, missingTeam);
    const partialScores = this.analyzePartialScores(availableData, missingTeam);
    const partialMVP = this.analyzePartialMVP(availableData, missingTeam);
    const partialEvidence = this.analyzePartialEvidence(availableData, missingTeam);
    
    return {
      comparisonType: COMPARISON_TYPES.PARTIAL_SUBMISSION,
      severity: CONFLICT_SEVERITY.LOW,
      autoApprovable: false,
      requiresManualReview: true,
      
      // Include analyzed data for display
      scores: partialScores,
      playerStats: partialPlayerStats,
      lineups: partialLineups,
      mvp: partialMVP,
      evidence: partialEvidence,
      
      partialSubmissionInfo: {
        missingTeam,
        availableData,
        canApprovePartial: true
      },
      
      recommendations: [
        `Only ${missingTeam === 'team1' ? 'Team 2' : 'Team 1'} has submitted results`,
        'You can approve this single submission or wait for both teams',
        'Consider contacting the missing team about their submission'
      ],
      
      conflictSummary: `Waiting for ${missingTeam === 'team1' ? 'Team 1' : 'Team 2'} submission`
    };
  }

  /**
   * Compares match scores between submissions
   */
  compareScores() {
    const team1Score1 = this.team1Data?.team1_score || 0;
    const team1Score2 = this.team1Data?.team2_score || 0;
    const team2Score1 = this.team2Data?.team1_score || 0;
    const team2Score2 = this.team2Data?.team2_score || 0;

    const scoresMatch = team1Score1 === team2Score1 && team1Score2 === team2Score2;
    
    return {
      status: scoresMatch ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
      team1Submission: { team1_score: team1Score1, team2_score: team1Score2 },
      team2Submission: { team1_score: team2Score1, team2_score: team2Score2 },
      difference: scoresMatch ? null : {
        team1_score_diff: team2Score1 - team1Score1,
        team2_score_diff: team2Score2 - team1Score2
      }
    };
  }

  /**
   * Compares individual player statistics
   */
  comparePlayerStats() {
    const team1Stats = this.team1Data?.playerStats || this.team1Data?.player_stats || [];
    const team2Stats = this.team2Data?.playerStats || this.team2Data?.player_stats || [];

    // Group stats by player_id for comparison
    const team1StatsByPlayer = this.groupStatsByPlayer(team1Stats);
    const team2StatsByPlayer = this.groupStatsByPlayer(team2Stats);

    const allPlayerIds = new Set([
      ...Object.keys(team1StatsByPlayer),
      ...Object.keys(team2StatsByPlayer)
    ]);

    const playerComparisons = [];
    const statFields = ['goals', 'assists', 'saves', 'passes', 'interceptions'];

    for (const playerId of allPlayerIds) {
      const team1PlayerStats = team1StatsByPlayer[playerId];
      const team2PlayerStats = team2StatsByPlayer[playerId];

      if (!team1PlayerStats || !team2PlayerStats) {
        playerComparisons.push({
          playerId,
          status: FIELD_COMPARISON.MISSING,
          missingFrom: !team1PlayerStats ? 'team1' : 'team2',
          availableStats: team1PlayerStats || team2PlayerStats
        });
        continue;
      }

      // Compare individual stat fields
      const fieldComparisons = {};
      let allFieldsMatch = true;

      for (const field of statFields) {
        const team1Value = team1PlayerStats[field] || 0;
        const team2Value = team2PlayerStats[field] || 0;
        const fieldsMatch = team1Value === team2Value;
        
        if (!fieldsMatch) allFieldsMatch = false;

        fieldComparisons[field] = {
          status: fieldsMatch ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
          team1Value,
          team2Value,
          difference: team2Value - team1Value
        };
      }

      playerComparisons.push({
        playerId,
        status: allFieldsMatch ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
        team1Stats: team1PlayerStats,
        team2Stats: team2PlayerStats,
        fieldComparisons,
        overallMatch: allFieldsMatch
      });
    }

    const allPlayersMatch = playerComparisons.every(p => p.status === FIELD_COMPARISON.MATCH);
    const missingPlayers = playerComparisons.filter(p => p.status === FIELD_COMPARISON.MISSING);
    const conflictedPlayers = playerComparisons.filter(p => p.status === FIELD_COMPARISON.DIFFER);

    return {
      status: allPlayersMatch ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
      playerComparisons,
      summary: {
        totalPlayers: allPlayerIds.size,
        matchingPlayers: playerComparisons.filter(p => p.status === FIELD_COMPARISON.MATCH).length,
        conflictedPlayers: conflictedPlayers.length,
        missingPlayers: missingPlayers.length
      },
      conflicts: conflictedPlayers,
      missing: missingPlayers
    };
  }

  /**
   * Compares team lineups against registered rosters
   */
  compareLineups() {
    const team1Stats = this.team1Data?.playerStats || this.team1Data?.player_stats || [];
    const team2Stats = this.team2Data?.playerStats || this.team2Data?.player_stats || [];

    const team1PlayerIds = [...new Set(team1Stats.map(s => s.player_id))];
    const team2PlayerIds = [...new Set(team2Stats.map(s => s.player_id))];

    // Validate against team rosters
    const team1Validation = this.validateLineupAgainstRoster(team1PlayerIds, this.team1Roster, 'team1');
    const team2Validation = this.validateLineupAgainstRoster(team2PlayerIds, this.team2Roster, 'team2');

    // Compare lineups between submissions
    const playersMatch = isEqual(
      team1PlayerIds.sort(), 
      team2PlayerIds.sort()
    );

    const onlyInTeam1 = team1PlayerIds.filter(id => !team2PlayerIds.includes(id));
    const onlyInTeam2 = team2PlayerIds.filter(id => !team1PlayerIds.includes(id));

    return {
      status: playersMatch && team1Validation.valid && team2Validation.valid 
        ? FIELD_COMPARISON.MATCH 
        : FIELD_COMPARISON.DIFFER,
      playersMatch,
      team1Validation,
      team2Validation,
      differences: {
        onlyInTeam1Submission: onlyInTeam1,
        onlyInTeam2Submission: onlyInTeam2
      },
      summary: {
        team1PlayerCount: team1PlayerIds.length,
        team2PlayerCount: team2PlayerIds.length,
        commonPlayers: team1PlayerIds.filter(id => team2PlayerIds.includes(id)).length
      }
    };
  }

  /**
   * Validates lineup against team roster
   */
  validateLineupAgainstRoster(playerIds, roster, teamKey) {
    if (!roster || roster.length === 0) {
      return {
        valid: true, // Can't validate without roster data
        reason: 'No roster data available for validation',
        invalidPlayers: [],
        validPlayers: playerIds
      };
    }

    const rosterPlayerIds = roster.map(member => member.user_id || member.id);
    const invalidPlayers = playerIds.filter(id => !rosterPlayerIds.includes(id));
    const validPlayers = playerIds.filter(id => rosterPlayerIds.includes(id));

    return {
      valid: invalidPlayers.length === 0,
      reason: invalidPlayers.length > 0 
        ? `${invalidPlayers.length} player(s) not in registered roster`
        : 'All players are registered team members',
      invalidPlayers,
      validPlayers,
      teamKey
    };
  }

  /**
   * Compares MVP selections
   */
  compareMVPs() {
    const team1MVP = this.team1Data?.mvpPlayerId || this.team1Data?.mvp_player_id;
    const team2MVP = this.team2Data?.mvpPlayerId || this.team2Data?.mvp_player_id;

    // Both null/undefined is considered a match
    if (!team1MVP && !team2MVP) {
      return {
        status: FIELD_COMPARISON.MATCH,
        team1MVP: null,
        team2MVP: null,
        match: true
      };
    }

    // One missing is a difference
    if (!team1MVP || !team2MVP) {
      return {
        status: FIELD_COMPARISON.DIFFER,
        team1MVP,
        team2MVP,
        match: false,
        reason: !team1MVP ? 'Team 1 did not select MVP' : 'Team 2 did not select MVP'
      };
    }

    // Both have MVP selections
    const mvpsMatch = team1MVP === team2MVP;
    
    return {
      status: mvpsMatch ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
      team1MVP,
      team2MVP,
      match: mvpsMatch,
      reason: mvpsMatch ? 'Both teams selected the same MVP' : 'Teams selected different MVPs'
    };
  }

  /**
   * Compares evidence/screenshots
   */
  compareEvidence() {
    const team1Screenshots = {
      team1: this.team1Data?.team1_screenshot_url,
      team2: this.team1Data?.team2_screenshot_url
    };

    const team2Screenshots = {
      team1: this.team2Data?.team1_screenshot_url,
      team2: this.team2Data?.team2_screenshot_url
    };

    const team1HasEvidence = !!(team1Screenshots.team1 || team1Screenshots.team2);
    const team2HasEvidence = !!(team2Screenshots.team1 || team2Screenshots.team2);

    const evidenceMatches = isEqual(team1Screenshots, team2Screenshots);

    return {
      status: evidenceMatches ? FIELD_COMPARISON.MATCH : FIELD_COMPARISON.DIFFER,
      team1Evidence: team1Screenshots,
      team2Evidence: team2Screenshots,
      team1HasEvidence,
      team2HasEvidence,
      evidenceMatches,
      summary: {
        bothHaveEvidence: team1HasEvidence && team2HasEvidence,
        neitherHasEvidence: !team1HasEvidence && !team2HasEvidence,
        onlyTeam1HasEvidence: team1HasEvidence && !team2HasEvidence,
        onlyTeam2HasEvidence: !team1HasEvidence && team2HasEvidence
      }
    };
  }


  /**
   * Validates statistical consistency
   */
  validateStatistics() {
    const team1Stats = this.team1Data?.playerStats || this.team1Data?.player_stats || [];
    const team2Stats = this.team2Data?.playerStats || this.team2Data?.player_stats || [];

    const team1Validation = this.validateStatsConsistency(
      team1Stats, 
      this.team1Data?.team1_score || 0,
      this.team1Data?.team2_score || 0
    );

    const team2Validation = this.validateStatsConsistency(
      team2Stats,
      this.team2Data?.team1_score || 0, 
      this.team2Data?.team2_score || 0
    );

    return {
      team1Valid: team1Validation.valid,
      team2Valid: team2Validation.valid,
      team1Validation,
      team2Validation,
      bothValid: team1Validation.valid && team2Validation.valid
    };
  }

  /**
   * Validates internal statistical consistency
   */
  validateStatsConsistency(playerStats, team1Score, team2Score) {
    if (!playerStats || playerStats.length === 0) {
      return {
        valid: true,
        reason: 'No player stats to validate',
        issues: []
      };
    }

    const issues = [];
    
    // Calculate total goals from player stats
    const totalGoals = playerStats.reduce((sum, player) => sum + (player.goals || 0), 0);
    
    // Goals should match team score (assuming this team's perspective)
    // This is tricky without knowing which team's perspective each submission represents
    // For now, we'll check if total goals is reasonable compared to both scores
    const maxPossibleGoals = Math.max(team1Score, team2Score);
    
    if (totalGoals > maxPossibleGoals * 2) { // Allow some flexibility
      issues.push({
        type: 'goals_too_high',
        message: `Total player goals (${totalGoals}) seems too high compared to match score`,
        severity: 'medium'
      });
    }

    // Check for impossible stat values
    playerStats.forEach((player, index) => {
      const playerIssues = [];
      
      if ((player.goals || 0) < 0) playerIssues.push('Negative goals');
      if ((player.assists || 0) < 0) playerIssues.push('Negative assists');
      if ((player.saves || 0) < 0) playerIssues.push('Negative saves');
      
      // Check for unreasonably high values
      if ((player.goals || 0) > 20) playerIssues.push('Unusually high goals');
      if ((player.assists || 0) > 20) playerIssues.push('Unusually high assists');
      if ((player.saves || 0) > 50) playerIssues.push('Unusually high saves');

      if (playerIssues.length > 0) {
        issues.push({
          type: 'player_stats_invalid',
          playerId: player.player_id,
          message: `Player ${index + 1}: ${playerIssues.join(', ')}`,
          severity: 'high'
        });
      }
    });

    return {
      valid: issues.length === 0,
      reason: issues.length === 0 ? 'All statistics appear consistent' : `${issues.length} statistical issue(s) found`,
      issues,
      totalGoals,
      playerCount: playerStats.length
    };
  }

  /**
   * Groups player stats by player_id
   */
  groupStatsByPlayer(playerStats) {
    return playerStats.reduce((acc, stats) => {
      acc[stats.player_id] = stats;
      return acc;
    }, {});
  }

  /**
   * Determines overall comparison type
   */
  determineComparisonType(comparisons) {
    const { scoreComparison, playerStatsComparison, lineupComparison, mvpComparison, evidenceComparison } = comparisons;

    // Perfect match: everything matches
    if (
      scoreComparison.status === FIELD_COMPARISON.MATCH &&
      playerStatsComparison.status === FIELD_COMPARISON.MATCH &&
      lineupComparison.status === FIELD_COMPARISON.MATCH &&
      mvpComparison.status === FIELD_COMPARISON.MATCH &&
      evidenceComparison.status === FIELD_COMPARISON.MATCH
    ) {
      return COMPARISON_TYPES.PERFECT_MATCH;
    }

    // Scores match but other differences
    if (scoreComparison.status === FIELD_COMPARISON.MATCH) {
      if (playerStatsComparison.status === FIELD_COMPARISON.DIFFER) {
        return COMPARISON_TYPES.SCORE_MATCH_STATS_DIFFER;
      }
    }

    // Lineup issues
    if (lineupComparison.status === FIELD_COMPARISON.DIFFER && !lineupComparison.playersMatch) {
      return COMPARISON_TYPES.LINEUP_MISMATCH;
    }

    // Statistical inconsistencies
    if (!comparisons.statisticalValidation?.bothValid) {
      return COMPARISON_TYPES.STATS_TOTAL_MISMATCH;
    }

    // MVP conflicts (but other things match)
    if (mvpComparison.status === FIELD_COMPARISON.DIFFER) {
      return COMPARISON_TYPES.MVP_CONFLICT;
    }

    // Evidence mismatch only
    if (evidenceComparison.status === FIELD_COMPARISON.DIFFER) {
      return COMPARISON_TYPES.EVIDENCE_MISMATCH;
    }

    // Everything is different
    return COMPARISON_TYPES.COMPLETELY_DIFFERENT;
  }

  /**
   * Calculates conflict severity
   */
  calculateSeverity(comparisons) {
    const { scoreComparison, playerStatsComparison, lineupComparison, statisticalValidation } = comparisons;

    // No conflicts
    if (
      scoreComparison.status === FIELD_COMPARISON.MATCH &&
      playerStatsComparison.status === FIELD_COMPARISON.MATCH &&
      lineupComparison.status === FIELD_COMPARISON.MATCH
    ) {
      return CONFLICT_SEVERITY.NONE;
    }

    // Critical: Score differences or invalid lineups
    if (
      scoreComparison.status === FIELD_COMPARISON.DIFFER ||
      (lineupComparison.status === FIELD_COMPARISON.DIFFER && !lineupComparison.playersMatch) ||
      !statisticalValidation?.bothValid
    ) {
      return CONFLICT_SEVERITY.CRITICAL;
    }

    // High: Major player stat differences
    if (playerStatsComparison.status === FIELD_COMPARISON.DIFFER && playerStatsComparison.summary.conflictedPlayers > 2) {
      return CONFLICT_SEVERITY.HIGH;
    }

    // Medium: Some player stat differences
    if (playerStatsComparison.status === FIELD_COMPARISON.DIFFER) {
      return CONFLICT_SEVERITY.MEDIUM;
    }

    // Low: Only MVP or evidence differences
    return CONFLICT_SEVERITY.LOW;
  }

  /**
   * Generates smart recommendations
   */
  generateRecommendations(comparisonType, severity) {
    const recommendations = [];

    switch (comparisonType) {
      case COMPARISON_TYPES.PERFECT_MATCH:
        recommendations.push('✅ All submissions match perfectly - can auto-approve');
        recommendations.push('Both teams agree on all match details');
        break;

      case COMPARISON_TYPES.SCORE_MATCH_STATS_DIFFER:
        recommendations.push('🏆 Match scores agree but player statistics differ');
        recommendations.push('Review individual player performance data');
        recommendations.push('Consider which submission has more detailed/accurate stats');
        break;

      case COMPARISON_TYPES.LINEUP_MISMATCH:
        recommendations.push('⚠️ Different players reported in submissions');
        recommendations.push('Verify team rosters and player eligibility');
        recommendations.push('Contact teams to clarify who actually played');
        break;

      case COMPARISON_TYPES.STATS_TOTAL_MISMATCH:
        recommendations.push('🚫 Statistical inconsistencies detected');
        recommendations.push('Player goals don\'t add up to team score');
        recommendations.push('Review for data entry errors');
        break;

      case COMPARISON_TYPES.MVP_CONFLICT:
        recommendations.push('🏅 Teams selected different MVPs');
        recommendations.push('Both MVPs performed well - either choice is valid');
        recommendations.push('Select submission with more complete data');
        break;

      case COMPARISON_TYPES.EVIDENCE_MISMATCH:
        recommendations.push('📸 Different evidence/screenshots provided');
        recommendations.push('Review visual evidence for accuracy');
        recommendations.push('Choose submission with clearer evidence');
        break;

      case COMPARISON_TYPES.COMPLETELY_DIFFERENT:
        recommendations.push('🔄 Major discrepancies across multiple areas');
        recommendations.push('Requires careful manual review');
        recommendations.push('Consider contacting both teams for clarification');
        break;

      case COMPARISON_TYPES.PARTIAL_SUBMISSION:
        recommendations.push('⏳ Only one team has submitted results');
        recommendations.push('Can approve single submission or wait for both');
        break;

      default:
        break;
    }

    // Add severity-based recommendations
    switch (severity) {
      case CONFLICT_SEVERITY.CRITICAL:
        recommendations.push('🚨 Critical conflicts require immediate attention');
        break;
      case CONFLICT_SEVERITY.HIGH:
        recommendations.push('⚠️ Significant differences need thorough review');
        break;
      case CONFLICT_SEVERITY.MEDIUM:
        recommendations.push('📝 Minor conflicts - review and select best submission');
        break;

      default:
        break;
    }

    return recommendations;
  }

  /**
   * Generates conflict summary
   */
  generateConflictSummary(comparisons) {
    const conflicts = [];

    if (comparisons.scoreComparison.status === FIELD_COMPARISON.DIFFER) {
      conflicts.push('Match scores differ');
    }

    if (comparisons.playerStatsComparison.status === FIELD_COMPARISON.DIFFER) {
      const conflictCount = comparisons.playerStatsComparison.summary.conflictedPlayers;
      conflicts.push(`${conflictCount} player(s) have different stats`);
    }

    if (comparisons.lineupComparison.status === FIELD_COMPARISON.DIFFER) {
      conflicts.push('Different players in lineups');
    }

    if (comparisons.mvpComparison.status === FIELD_COMPARISON.DIFFER) {
      conflicts.push('Different MVP selections');
    }

    if (comparisons.evidenceComparison.status === FIELD_COMPARISON.DIFFER) {
      conflicts.push('Different evidence provided');
    }

    if (conflicts.length === 0) {
      return 'All submissions match perfectly';
    }

    return conflicts.join(' • ');
  }
}

/**
 * Utility functions for external use
 */

/**
 * Quick comparison check for auto-approval logic
 */
export const canAutoApprove = (team1Submission, team2Submission, teamRosters = {}) => {
  const engine = new ComparisonEngine(team1Submission, team2Submission, teamRosters);
  const analysis = engine.analyzeSubmissions();
  return analysis.autoApprovable;
};

/**
 * Helper methods for analyzing partial submissions
 */

/**
 * Analyze player stats from partial submission
 */
ComparisonEngine.prototype.analyzePartialPlayerStats = function(availableData, missingTeam) {
  const playerStats = availableData?.playerStats || availableData?.player_stats || [];
  
  if (playerStats.length === 0) {
    return null;
  }

  // For partial submissions, show players as available (not missing)
  // since we're reviewing what was actually submitted
  const playerComparisons = playerStats.map(stats => ({
    playerId: stats.player_id,
    status: FIELD_COMPARISON.MATCH, // Show as available data, not missing
    availableStats: stats,
    [missingTeam === 'team1' ? 'team2Stats' : 'team1Stats']: stats,
    [missingTeam === 'team1' ? 'team1Stats' : 'team2Stats']: null,
    // Mark as partial submission player
    isPartialSubmissionPlayer: true
  }));

  return {
    status: FIELD_COMPARISON.MISSING, // Overall status is still missing (no comparison possible)
    playerComparisons,
    summary: {
      totalPlayers: playerStats.length,
      matchingPlayers: playerStats.length, // All available players are "matching" themselves
      conflictedPlayers: 0,
      missingPlayers: 0 // No missing players to show
    },
    conflicts: [],
    missing: [],
    isPartialSubmission: true,
    submittingTeam: missingTeam === 'team1' ? 'team2' : 'team1'
  };
};

/**
 * Analyze lineups from partial submission  
 */
ComparisonEngine.prototype.analyzePartialLineups = function(availableData, missingTeam) {
  const playerStats = availableData?.playerStats || availableData?.player_stats || [];
  const playerIds = [...new Set(playerStats.map(s => s.player_id))];
  
  if (playerIds.length === 0) {
    return null;
  }

  // Create lineup validation for available team
  const availableTeamRoster = missingTeam === 'team1' ? this.team2Roster : this.team1Roster;
  const availableTeamValidation = this.validateLineupAgainstRoster(playerIds, availableTeamRoster, missingTeam === 'team1' ? 'team2' : 'team1');
  
  // Extract player names for better display
  const playerDetails = playerStats.map(stats => ({
    id: stats.player_id,
    name: stats.player_name || stats.username || `Player ${stats.player_id.slice(-4)}`,
    avatar: stats.player_avatar
  }));
  
  // Create comparison structure for partial submission
  return {
    status: FIELD_COMPARISON.MATCH, // Show as valid lineup review (not missing)
    playersMatch: null, // Not applicable for single submission
    [missingTeam === 'team1' ? 'team2Validation' : 'team1Validation']: availableTeamValidation,
    [missingTeam === 'team1' ? 'team1Validation' : 'team2Validation']: {
      valid: null, // Not applicable
      reason: 'No submission from this team',
      invalidPlayers: [],
      validPlayers: []
    },
    differences: {
      submittedPlayers: playerDetails, // Show player names instead of IDs
      submittingTeamName: missingTeam === 'team1' ? 'team2' : 'team1',
      // Remove the confusing onlyInTeam1/Team2 structure for partial submissions
    },
    summary: {
      team1PlayerCount: missingTeam === 'team1' ? 0 : playerIds.length,
      team2PlayerCount: missingTeam === 'team1' ? playerIds.length : 0,
      commonPlayers: 0
    },
    isPartialSubmission: true,
    submittingTeam: missingTeam === 'team1' ? 'team2' : 'team1'
  };
};

/**
 * Analyze scores from partial submission
 */
ComparisonEngine.prototype.analyzePartialScores = function(availableData, missingTeam) {
  const team1Score = availableData?.team1_score || 0;
  const team2Score = availableData?.team2_score || 0;
  
  return {
    status: FIELD_COMPARISON.MISSING,
    [missingTeam === 'team1' ? 'team2Submission' : 'team1Submission']: { 
      team1_score: team1Score, 
      team2_score: team2Score 
    },
    [missingTeam === 'team1' ? 'team1Submission' : 'team2Submission']: { 
      team1_score: 0, 
      team2_score: 0 
    },
    difference: null
  };
};

/**
 * Analyze MVP from partial submission
 */
ComparisonEngine.prototype.analyzePartialMVP = function(availableData, missingTeam) {
  const mvpId = availableData?.mvpPlayerId || availableData?.mvp_player_id;
  
  return {
    status: FIELD_COMPARISON.MISSING,
    [missingTeam === 'team1' ? 'team2MVP' : 'team1MVP']: mvpId,
    [missingTeam === 'team1' ? 'team1MVP' : 'team2MVP']: null,
    match: false,
    reason: `Only ${missingTeam === 'team1' ? 'Team 2' : 'Team 1'} selected MVP`
  };
};

/**
 * Analyze evidence from partial submission
 */
ComparisonEngine.prototype.analyzePartialEvidence = function(availableData, missingTeam) {
  const screenshots = {
    team1: availableData?.team1_screenshot_url,
    team2: availableData?.team2_screenshot_url
  };
  
  const hasEvidence = !!(screenshots.team1 || screenshots.team2);
  
  return {
    status: FIELD_COMPARISON.MISSING,
    [missingTeam === 'team1' ? 'team2Evidence' : 'team1Evidence']: screenshots,
    [missingTeam === 'team1' ? 'team1Evidence' : 'team2Evidence']: { team1: null, team2: null },
    [missingTeam === 'team1' ? 'team2HasEvidence' : 'team1HasEvidence']: hasEvidence,
    [missingTeam === 'team1' ? 'team1HasEvidence' : 'team2HasEvidence']: false,
    evidenceMatches: false,
    summary: {
      bothHaveEvidence: false,
      neitherHasEvidence: !hasEvidence,
      onlyTeam1HasEvidence: missingTeam === 'team2' && hasEvidence,
      onlyTeam2HasEvidence: missingTeam === 'team1' && hasEvidence
    }
  };
};